'use client';

/**
 * Presentational table + badges for the teacher assignments list.
 *
 * Module: app/assignments/assignment-table
 * Layer:  component (presentational)
 *
 * Purpose:
 *     Renders the assignment roster as a clickable table — title, classroom,
 *     due date, type badge, submission/grading progress, and status badge —
 *     plus the small reusable type/status badge components. Pure presentation:
 *     all data + handlers arrive via props so each function clears the
 *     CC<=5 / <=60-line gates.
 *
 * AI context:
 *     - Entry points called by: src/app/assignments/page.tsx.
 *     - Depends on: lib/assignments (types + label maps), lib/student-helpers
 *       (date formatter), next/link, useTranslation.
 *     - Side effects: none (navigation is delegated to next/link).
 *     - Invariants: every visible string is from the i18n catalogue.
 *     - Replacement cost: LOW.
 */

import type { Route } from 'next';
import Link from 'next/link';
import type React from 'react';
import {
  type Assignment,
  ASSIGNMENT_STATUS_KEY,
  ASSIGNMENT_TYPE_KEY,
} from '@/lib/assignments';
import { useTranslation } from '@/lib/i18n';
import { formatDate } from '@/lib/student-helpers';

/** A small pill badge for an assignment's type. */
export function TypeBadge({ type }: { readonly type: Assignment['type'] }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className='rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-medium'>
      {t(ASSIGNMENT_TYPE_KEY[type])}
    </span>
  );
}

/** A small pill badge for an assignment's lifecycle status. */
export function StatusBadge({
  status,
}: {
  readonly status: Assignment['status'];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className='rounded-full border border-border px-2.5 py-0.5 text-xs font-medium'>
      {t(ASSIGNMENT_STATUS_KEY[status])}
    </span>
  );
}

/** One assignment row, linking to the detail page. */
function AssignmentRow({ assignment }: { readonly assignment: Assignment }): React.JSX.Element {
  const { t } = useTranslation();
  const href = `/assignments/${String(assignment.id)}` as Route;
  const due =
    assignment.due_date === null
      ? t('assignments.teacher.noDueDate')
      : formatDate(assignment.due_date);
  const progress = t('assignments.teacher.progressLabel', {
    submitted: assignment.submission_count,
    total: assignment.submission_count,
    graded: assignment.graded_count,
  });
  return (
    <tr className='border-b border-border last:border-0 hover:bg-foreground/5'>
      <td className='p-3'>
        <Link href={href} className='font-medium hover:underline'>
          {assignment.title}
        </Link>
      </td>
      <td className='p-3 text-sm opacity-70'>{assignment.classroom_name}</td>
      <td className='p-3 text-sm opacity-70'>{due}</td>
      <td className='p-3'>
        <TypeBadge type={assignment.type} />
      </td>
      <td className='p-3 text-sm tabular-nums opacity-70'>{progress}</td>
      <td className='p-3'>
        <StatusBadge status={assignment.status} />
      </td>
    </tr>
  );
}

/** Column header keys rendered in display order (data-driven). */
const COLUMN_KEYS = [
  'assignments.teacher.col.title',
  'assignments.teacher.col.classroom',
  'assignments.teacher.col.due',
  'assignments.teacher.col.type',
  'assignments.teacher.col.progress',
  'assignments.teacher.col.status',
] as const;

/** The full assignments table. */
export function AssignmentTable({
  assignments,
}: {
  readonly assignments: readonly Assignment[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='overflow-x-auto rounded-xl border border-border'>
      <table className='w-full border-collapse text-left'>
        <thead>
          <tr className='border-b border-border bg-foreground/5'>
            {COLUMN_KEYS.map((key) => (
              <th key={key} className='p-3 text-sm font-semibold'>
                {t(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {assignments.map((assignment) => (
            <AssignmentRow key={assignment.id} assignment={assignment} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
